<template>
	<div class="showroom-filter">
		<!-- Search + Product Type Filter -->
		<div class="filter-controls mb-4">
			<div class="search-box-wrapper text-center">
				<div class="search-box">
					<i class="bi bi-search"></i>
					<input
						v-model="searchQuery"
						type="text"
						class="form-control"
						placeholder="Search by name, SKU, display code, or dimensions..."
						@input="debouncedSearch"
					/>
					<button
						v-if="searchQuery"
						@click="clearSearch"
						class="clear-search"
						aria-label="Clear search"
					>
						<i class="bi bi-x-circle"></i>
					</button>
				</div>
			</div>

			<div class="product-type-filter-wrapper text-center">
				<select
					v-model="selectedProductType"
					class="form-select product-type-select"
					aria-label="Filter by Product Type"
				>
					<option value="">All Product Types</option>
					<option
						v-for="productType in productTypeOptions"
						:key="productType.slug"
						:value="productType.slug"
					>
						{{ productType.name }}
					</option>
				</select>
			</div>
		</div>

		<!-- Grid of Cards -->
		<div class="row g-4 justify-content-center">
			<div
				v-for="(showroom, index) in filteredShowrooms"
				:key="showroom.id"
				class="col-12 col-md-6 col-lg-4"
			>
				<div
					class="display-card h-100"
					data-bs-toggle="modal"
					:data-bs-target="'#showroomModal-' + showroom.id"
				>
					<div class="card-image">
						<img
							v-if="showroom.image"
							:src="showroom.image.url"
							:alt="showroom.image.alt || showroom.title || 'Showroom Display'"
						/>
						<div v-else class="no-image">
							<i class="bi bi-image"></i>
						</div>
					</div>
					<h3 v-if="showroom.title" class="card-title">{{ showroom.title }}</h3>
					<div class="card-content" v-html="showroom.short_description"></div>
				</div>
			</div>
		</div>

		<!-- No Results -->
		<div v-if="filteredShowrooms.length === 0" class="no-results text-center py-5">
			<i class="bi bi-search"></i>
			<h3>No displays found</h3>
			<p>Try adjusting your search terms</p>
			<button @click="clearSearch" class="btn btn-primary">Clear Search</button>
		</div>

		<!-- Modals -->
		<div
			v-for="showroom in filteredShowrooms"
			:key="'modal-' + showroom.id"
			class="modal fade showroom-modal"
			:id="'showroomModal-' + showroom.id"
			tabindex="-1"
			aria-hidden="true"
		>
			<div class="modal-dialog modal-xl modal-dialog-centered">
				<div class="modal-content border-0 shadow">
					<div class="modal-header border-0 pb-0">
						<button
							type="button"
							class="btn-close"
							data-bs-dismiss="modal"
							aria-label="Close"
						></button>
					</div>
					<div class="modal-body p-4 p-lg-4 pt-0">
						<div class="row align-items-start">
							<div class="col-lg-6 mb-4 mb-lg-0">
								<!-- Hotspot Viewer (if hotspots are defined) -->
								<HotspotViewer
									v-if="showroom.hotspot_data"
									:image="showroom.hotspot_data.image"
									:items="showroom.hotspot_data.items"
									:hotspots="showroom.hotspot_data.hotspots"
									:image-alt="showroom.title + ' display with hotspots'"
								/>
								<!-- Regular Image (no hotspots) -->
								<div v-else class="modal-image zoom-container">
									<img
										v-if="showroom.image"
										:src="showroom.image.url"
										:alt="
											showroom.image.alt ||
											showroom.title ||
											'Showroom Display'
										"
										class="img-fluid zoomable-image"
									/>
									<div class="zoom-hint">Click to zoom</div>
								</div>
							</div>
							<div class="col-lg-6">
								<div class="modal-text">
									<h2 v-if="showroom.title" class="modal-title mb-3">
										{{ showroom.title }}
									</h2>
									<div v-html="showroom.content"></div>

									<!-- Display metadata table -->
									<table
										v-if="
											showroom.display_dimensions ||
											showroom.sku ||
											showroom.display_code ||
											showroom.link
										"
										class="showroom-details-table mt-4"
									>
										<tbody>
											<tr v-if="showroom.display_dimensions">
												<th>Display Dimensions</th>
												<td>{{ showroom.display_dimensions }}</td>
											</tr>
											<tr v-if="showroom.sku">
												<th>Item #</th>
												<td>{{ showroom.sku }}</td>
											</tr>
											<tr v-if="showroom.display_code">
												<th>Display Code</th>
												<td>{{ showroom.display_code }}</td>
											</tr>
											<tr v-if="showroom.link">
												<th>Link</th>
												<td>
													<a
														:href="showroom.link"
														target="_blank"
														rel="noopener"
														class="more-info-link"
													>
														More Info
														<i class="bi bi-box-arrow-up-right"></i>
													</a>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { ref, computed } from 'vue';
import HotspotViewer from './HotspotViewer.vue';

export default {
	name: 'ShowroomFilter',
	components: {
		HotspotViewer,
	},
	props: {
		showrooms: {
			type: Array,
			required: true,
			default: () => [],
		},
	},
	setup(props) {
		const searchQuery = ref('');
		const selectedProductType = ref('');
		let searchDebounce = null;

		const productTypeOptions = computed(() => {
			const termMap = new Map();

			props.showrooms.forEach(showroom => {
				(showroom.display_types || []).forEach(term => {
					if (term?.slug && term?.name && !termMap.has(term.slug)) {
						termMap.set(term.slug, {
							slug: term.slug,
							name: term.name,
						});
					}
				});
			});

			return Array.from(termMap.values()).sort((a, b) => a.name.localeCompare(b.name));
		});

		// Computed filtered showrooms
		const filteredShowrooms = computed(() => {
			const query = searchQuery.value.toLowerCase().trim();
			const activeProductType = selectedProductType.value;

			return props.showrooms.filter(showroom => {
				const matchesProductType =
					!activeProductType ||
					(showroom.display_types || []).some(term => term?.slug === activeProductType);

				if (!matchesProductType) {
					return false;
				}

				if (!query) {
					return true;
				}

				// Build searchable text from all relevant fields
				const searchableText = [
					showroom.title,
					showroom.sku,
					showroom.display_code,
					showroom.display_dimensions,
					(showroom.display_types || []).map(term => term?.name).join(' '),
					showroom.short_description_stripped,
					showroom.content_stripped,
				]
					.filter(Boolean)
					.join(' ')
					.toLowerCase();

				return searchableText.includes(query);
			});
		});

		// Debounced search handler
		const debouncedSearch = () => {
			clearTimeout(searchDebounce);
			searchDebounce = setTimeout(() => {
				// Search is reactive via computed, no action needed
			}, 300);
		};

		// Clear search
		const clearSearch = () => {
			searchQuery.value = '';
		};

		return {
			searchQuery,
			selectedProductType,
			productTypeOptions,
			filteredShowrooms,
			debouncedSearch,
			clearSearch,
		};
	},
};
</script>

<style scoped>
/* Component styles in _showroom-filter.scss */
</style>
